import { useEffect, useRef, useState } from 'react';
import { formatDate } from '../lib/dates';

/**
 * A date that reads as dd/mm/yyyy and becomes a picker when you go to change it.
 *
 * A native `<input type="date">` paints its own text in the BROWSER's locale — yyyy/mm/dd on
 * one machine, mm/dd/yyyy on another — and nothing in CSS or markup can change that. So the
 * resting state here is our own text, and the native control appears only while editing, where
 * its calendar is worth having and its format no longer matters.
 */
export function DateCell({
  value,
  editable,
  min,
  max,
  emphasis,
  title,
  emptyLabel,
  onChange,
}: {
  value: string | null;
  editable: boolean;
  min?: string;
  max?: string;
  /** Overdue, typically: the date itself carries the warning rather than a separate badge. */
  emphasis?: boolean;
  title?: string;
  /**
   * What an empty value reads as. A dash suits a table cell, where the column already says
   * what is missing; a form field wants an instruction — "Pick a date" — because a lone dash
   * beside a label looks like something has gone wrong rather than like an invitation.
   */
  emptyLabel?: string;
  onChange: (next: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) return;
    input.current?.focus();

    /**
     * Opening the calendar is a nicety, and `showPicker()` THROWS without user activation —
     * `NotAllowedError`, which took the whole cell into the error boundary when it happened.
     * Focused is enough on its own; clicking the field opens the calendar anyway.
     */
    try {
      input.current?.showPicker?.();
    } catch {
      /* no user gesture, or the browser will not open it programmatically */
    }
  }, [editing]);

  if (editing) {
    return (
      <input
        ref={input}
        type="date"
        className="cell-date"
        defaultValue={value ?? ''}
        min={min}
        max={max}
        onChange={(event) => {
          onChange(event.target.value || null);
          setEditing(false);
        }}
        // Leaving without choosing anything is a cancel, not a clear.
        onBlur={() => setEditing(false)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setEditing(false);
        }}
      />
    );
  }

  const text = value ? formatDate(value) : (emptyLabel ?? '—');
  const style = emphasis ? { color: 'var(--accent)', fontWeight: 600 } : undefined;

  if (!editable) {
    return (
      <span className="mono" style={style} title={title}>
        {text}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={value ? 'cell-date-button mono' : 'cell-date-button mono is-empty'}
      style={style}
      title={title ?? (value ? 'Change this date' : 'Set a date')}
      onClick={() => setEditing(true)}
    >
      {text}
    </button>
  );
}
